import { createStyles, Skeleton, Text } from '@mantine/core';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserRole } from 'src/enums/User';
import { useAuth } from 'src/hooks/useAuth';
import { useChat } from 'src/hooks/useChat';
import { ChatSession } from 'src/interfaces/Chat';
import { authRoutes } from 'src/navigators/routes/auth';
import { userRoutes } from 'src/navigators/routes/user';
import {
  clearChatState,
  resetUser,
  setSelectedSession,
} from 'src/redux/redux-store';
import { useAppDispatch, useAppSelector } from 'src/redux/redux-store/hooks';
import { ChatSessionsList } from './Chat/ChatSessionsList';
import { CustomButton } from './common/Buttons/CustomButton';
import { ConfirmFeedbackModal } from './Modals/ConfirmationModal';

interface SideBarProps {
  opened?: boolean;
  closeSidebar?: () => void;
  // subjects?: string[];
  // activeSubject is now managed by Redux
  // onSubjectChange?: (subject: string) => void;
}

const useStyles = createStyles((theme) => ({
  parentContainer: {
    display: 'flex',
    flexDirection: 'column',

    backgroundColor: 'white',
  },
}));

const Sidebar: React.FC<SideBarProps> = () => {
  const activeSubject = useAppSelector((state) => state.chat.activeSubject);
  const chatSessions = useAppSelector((state) => state.chat.sessions);
  const selectedSession = useAppSelector((state) => state.chat.selectedSession);
  const sessionsLoading = useAppSelector((state) => state.chat.sessionsLoading);
  const dispatch = useAppDispatch();
  const [isSignOutModalOpen, setIsSignOutModalOpen] = useState(false);
  const { classes } = useStyles();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.user);
  const { newSession, deleteSession } = useChat();
  const { logout } = useAuth();
  // Add loading state
  const [pageLoading, setPageLoading] = useState(true);
  // Selected child
  let selectedChild: string;
  if (user.role === UserRole.parent) {
    selectedChild = useAppSelector((state) => state.child.selectedChild);
  } else {
    selectedChild = user.name;
  }

  // useViewportHeight();

  // Add loading effect
  useEffect(() => {
    setPageLoading(sessionsLoading || !activeSubject);
  }, [sessionsLoading, activeSubject]);

  const groupedChats = useMemo(() => {
    const now = new Date();
    const today = now.toDateString();
    const yesterday = new Date(now.setDate(now.getDate() - 1)).toDateString();

    return chatSessions.reduce<Record<string, ChatSession[]>>((acc, chat) => {
      const chatDate = new Date(chat.created_at);
      let group: string;

      if (chatDate.toDateString() === today) {
        group = 'Today';
      } else if (chatDate.toDateString() === yesterday) {
        group = 'Yesterday';
      } else {
        group = 'Last Week';
      }

      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(chat);
      return acc;
    }, {});
  }, [chatSessions]);

  const selectSession = async (chatSession: ChatSession) => {
    dispatch(setSelectedSession(chatSession));
  };

  const handleSignOut = async () => {
    dispatch(resetUser());
    dispatch(clearChatState());
    logout();
    navigate(authRoutes.LOGIN, { replace: true });
  };

  const signOut = async () => {
    setIsSignOutModalOpen(true);
  };

  return (
    <div className="flex h-dvh flex-col text-gray-800 bg-white w-full p-4 pt-2 md:pt-0">
      <ConfirmFeedbackModal
        title="Sign Out"
        feedback="Are you sure you want to sign out?"
        onConfirm={handleSignOut}
        onClose={() => setIsSignOutModalOpen(false)}
        visible={isSignOutModalOpen}
        confirmLabel="Sign Out"
      />

      <div className="flex items-center space-x-3 mb-5 md:my-5">
        <div>
          <Text className="font-bold text-xl">{user.name}</Text>
          <Text className="text-sm text-gray-600">{user.level}</Text>
          <Text className="text-sm text-gray-600">{activeSubject}</Text>
        </div>
      </div>
      <div>
        <CustomButton
          variant="outline"
          size="default"
          alignment="left"
          className="text-sm w-full mb-2"
          // add query param child
          onClick={() =>
            navigate({
              pathname: userRoutes.PROGRESS.replace('/parent', ''),
              search: `?child=${selectedChild}`,
            })
          }
        >
          View Progress Report
        </CustomButton>
      </div>

      <div className="flex-grow min-h-0 flex flex-col">
        {pageLoading ? (
          <div className="space-y-4 p-4">
            <Skeleton height={20} width={80} radius="md" className="mb-2" />
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex items-center p-2 rounded-lg hover:bg-gray-50"
              >
                <Skeleton height={24} width={24} radius="sm" className="mr-3" />
                <div className="flex-1">
                  <Skeleton
                    height={16}
                    width="80%"
                    radius="md"
                    className="mb-1"
                  />
                  <Skeleton height={12} width="60%" radius="md" />
                </div>
              </div>
            ))}

            <Skeleton
              height={20}
              width={100}
              radius="md"
              className="mb-2 mt-4"
            />
            {[1, 2].map((i) => (
              <div
                key={`yesterday-${i}`}
                className="flex items-center p-2 rounded-lg hover:bg-gray-50"
              >
                <Skeleton height={24} width={24} radius="sm" className="mr-3" />
                <div className="flex-1">
                  <Skeleton
                    height={16}
                    width="70%"
                    radius="md"
                    className="mb-1"
                  />
                  <Skeleton height={12} width="50%" radius="md" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <ChatSessionsList
            groupedChats={groupedChats}
            onNewSession={newSession}
            onDeleteSession={deleteSession}
            onSelectSession={selectSession}
            selectedSession={selectedSession.id}
            maxHeight="calc(100vh - 36px)"
          />
        )}
      </div>

      <div className="mt-4">
        <CustomButton
          onClick={signOut}
          variant="ghost"
          size="default"
          leftIcon="logout"
          alignment="left"
          className="text-sm w-full"
        >
          Sign Out
        </CustomButton>
      </div>
    </div>
  );
};

export default Sidebar;
